/* eslint-env browser */
/**
 * Microsoft Entra ID (Azure AD) MSAL Configuration
 *
 * This module provides MSAL (Microsoft Authentication Library) configuration
 * for Entra ID authentication. It creates and exports the MSAL instance
 * and login request configuration.
 *
 * @module lib/authentication/entra/msalConfig
 */
// eslint-disable-next-line import/no-unresolved
import { PublicClientApplication } from '@azure/msal-browser';

// Get the redirect URI - use the configured redirect URI from Azure
// Note: The redirect URI in Azure must match exactly: https://dev.trusolutions.com/auth/callback.html
// Azure AD does not support hash fragments (#) in redirect URIs, so we use path-based routing.
// Using .html extension avoids S3/CloudFront path ambiguity (auth/callback vs auth/callback/).
const getRedirectUri = function getRedirectUri() {
  let win = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : typeof window !== 'undefined' ? window : undefined;
  let origin;
  if (win && win.location) {
    if (win.location.hostname === 'dev.trusolutions.com') {
      origin = 'https://dev.trusolutions.com';
    } else {
      origin = win.location.origin;
    }
  } else {
    origin = 'http://localhost';
  }
  return "".concat(origin, "/auth/callback.html");
};

/**
 * Get MSAL configuration for a customer
 * Customer settings are retrieved from server via RFC 8414 discovery endpoint
 *
 * @param {Object} customerSettings - Customer-specific Entra settings from discovery endpoint
 *   { tenantId, clientId, authority }
 * @returns {Object} MSAL configuration object
 * @throws {Error} If customerSettings are not provided or incomplete
 */
const getMsalConfig = function getMsalConfig(customerSettings) {
  let win = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : typeof window !== 'undefined' ? window : undefined;
  if (!customerSettings || !customerSettings.tenantId || !customerSettings.clientId) {
    throw new Error('Customer Entra settings (tenantId, clientId) are required');
  }
  return {
    auth: {
      clientId: customerSettings.clientId,
      authority: customerSettings.authority || "https://login.microsoftonline.com/".concat(customerSettings.tenantId),
      redirectUri: getRedirectUri(win)
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false
    }
  };
};

// Add scopes here for ID token to be used at Microsoft identity platform endpoints.
const loginRequest = {
  scopes: ['openid', 'profile', 'email']
};

// Create the main MSAL instance only in browser environment
// Check for browser crypto API (not available in Node.js/JSDOM test environments)
const isBrowserEnvironment = typeof window !== 'undefined' && window.crypto && window.crypto.subtle && typeof window.crypto.subtle !== 'undefined';

/**
 * Create MSAL instance for a customer
 * Customer settings are retrieved from server via RFC 8414 discovery endpoint
 * Components should call this function when they have customer settings from discovery
 *
 * @param {Object} customerSettings - Customer-specific Entra settings from discovery endpoint
 *   { tenantId, clientId, authority }
 * @param {Object} [options] - Optional overrides for testing (e.g. { forceBrowser: true })
 * @returns {PublicClientApplication|null} MSAL instance or null if not in browser environment
 */
const createMsalInstance = function createMsalInstance(customerSettings) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const useBrowser = options.forceBrowser === true ? true : isBrowserEnvironment;
  if (!useBrowser) {
    return null;
  }
  try {
    const config = getMsalConfig(customerSettings, options.window);
    const instance = new PublicClientApplication(config);
    // Do NOT initialize here - let the component handle initialization
    // This prevents race conditions and ensures proper timing
    return instance;
  } catch (error) {
    // MSAL initialization failed (likely in test environment or missing settings)
    // eslint-disable-next-line no-console
    console.warn('MSAL instance creation failed:', error.message);
    return null;
  }
};

// MSAL instance is created dynamically per customer using createMsalInstance()
// Customer settings are retrieved from server via RFC 8414 discovery endpoint
// Components should create the instance when they have customer settings from discovery
const msalInstance = null;
export { msalInstance, loginRequest, getMsalConfig, createMsalInstance };